import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreferencesDialog } from 'src/blocks/preferences-dialog';
import type { PreferencesOutput } from 'src/blocks/preferences-dialog/types';

import {
  applyPreferences,
  buildPreferencesSections,
  toStoredPreferences,
} from './applyPreferences';
import { useRiskOverviewPanelPreferences } from './hooks/useRiskOverviewPanelPreferences';
import { useRiskOverviewPanelSections } from './hooks/useRiskOverviewPanelSections';
import { RiskOverviewPanelView } from './RiskOverviewPanelView';
import type { RiskOverviewPanelProps } from './types';

const RiskOverviewPanel = ({ riskId }: RiskOverviewPanelProps) => {
  const { t: panelT } = useTranslation('common', {
    keyPrefix: 'riskOverviewPanel',
  });
  const { t: dialogT } = useTranslation('common', {
    keyPrefix: 'preferencesDialog',
  });

  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const { loading, sections } = useRiskOverviewPanelSections(riskId);
  const { preferences, setPreferences } = useRiskOverviewPanelPreferences();

  const panelSections = useMemo(
    () => applyPreferences(sections, preferences),
    [sections, preferences]
  );

  const preferencesSections = useMemo(
    () => buildPreferencesSections(sections, preferences),
    [sections, preferences]
  );

  const handleSave = (output: PreferencesOutput) => {
    setPreferences(toStoredPreferences(output.sections));
    setPreferencesOpen(false);
  };

  const panelLang = {
    title: panelT('title'),
    resultDatePrefix: panelT('resultDatePrefix'),
    preferencesAriaLabel: panelT('preferencesAriaLabel'),
    emptyStateMessage: panelT('emptyStateMessage'),
    notRatedBadge: panelT('notRatedBadge'),
    noRatingScore: panelT('noRatingScore'),
    noRatingAvailable: panelT('noRatingAvailable'),
  };

  const dialogLang = {
    title: dialogT('title'),
    description: dialogT('description'),
    save: dialogT('save'),
    cancel: dialogT('cancel'),
  };

  return (
    <>
      <RiskOverviewPanelView
        lang={panelLang}
        state={{ loading, sections: panelSections }}
        actions={{ onPreferencesClick: () => setPreferencesOpen(true) }}
      />
      <PreferencesDialog
        lang={dialogLang}
        state={{ open: preferencesOpen, sections: preferencesSections }}
        actions={{ onOpenChange: setPreferencesOpen, onSave: handleSave }}
      />
    </>
  );
};

export { RiskOverviewPanel };
export { RiskOverviewPanelView } from './RiskOverviewPanelView';
